/**
 * WHAT: Main dashboard displaying UAR campaign overview and compliance metrics.
 * WHY: Gives administrators an at-a-glance view of campaign status distribution
 *      and compliance framework coverage to prioritize attestation work.
 */

import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import axios from 'axios';

import API_CONFIG from '../config/api.config';
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Dashboard component showing campaign statistics, compliance framework
 *       distribution, and a table of all UAR campaigns.
 * WHY: Provides campaign managers with a centralized view of all user access
 *      review campaigns so they can track progress and identify bottlenecks.
 */
const Dashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    completed_campaigns: 0,
    attestation_campaigns: 0
  });
  const [complianceStats, setComplianceStats] = useState({});

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();

      if (data.success) {
        setCampaigns(data.campaigns || []);

        // Calculate stats
        const total = data.campaigns.length;
        const active = data.campaigns.filter(c => c.status === 'ACTIVE' || c.status === 'CREATED' || c.status === 'RUNNING').length;
        const completed = data.campaigns.filter(c => c.status === 'COMPLETED').length;
        const attestation = data.campaigns.filter(c => c.status === 'ATTESTATION').length;

        setStats({
          total_campaigns: total,
          active_campaigns: active,
          completed_campaigns: completed,
          attestation_campaigns: attestation
        });

        // Calculate compliance framework stats
        const complianceCounts = {};
        data.campaigns.forEach(c => {
          const framework = c.compliance_framework || c.compliance_key || 'Unassigned';
          complianceCounts[framework] = (complianceCounts[framework] || 0) + 1;
        });
        setComplianceStats(complianceCounts);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badgeClass = 'badge';

    switch (status) {
      case 'ACTIVE':
      case 'RUNNING':
        return <span className={`${badgeClass} badge-primary`}>
          <i className="fas fa-play-circle me-1"></i>{status === 'ACTIVE' ? 'Active' : 'Running'}
        </span>;
      case 'CREATED':
        return <span className={`${badgeClass} badge-info`}>
          <i className="fas fa-plus-circle me-1"></i>Created
        </span>;
      case 'ATTESTATION':
        return <span className={`${badgeClass} badge-warning`}>
          <i className="fas fa-clipboard-check me-1"></i>Attestation
        </span>;
      case 'COMPLETED':
        return <span className={`${badgeClass} badge-success`}>
          <i className="fas fa-check-circle me-1"></i>Completed
        </span>;
      case 'CLOSED':
        return <span className={`${badgeClass} badge-secondary`}>
          <i className="fas fa-times-circle me-1"></i>Closed
        </span>;
      case 'CANCELLED':
        return <span className={`${badgeClass} badge-danger`}>
          <i className="fas fa-ban me-1"></i>Cancelled
        </span>;
      default:
        return <span className={`${badgeClass} badge-secondary`}>{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{
        minHeight: '100vh',
        backgroundColor: 'var(--background-secondary)'
      }}>
        <div className="spinner-border" style={{ color: 'var(--primary-500)' }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--background-secondary)',
      minHeight: '100vh',
      padding: 'var(--space-2xl)'
    }}>
      {/* Page Header */}
      <div className="d-flex justify-content-end mb-4">
        <button className="btn btn-primary shadow-sm" onClick={fetchCampaigns} style={{
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-sm) var(--space-lg)',
          fontWeight: 'var(--font-weight-semibold)'
        }}>
          <i className="fas fa-sync me-2"></i>
          Refresh
        </button>
      </div>

      {/* Campaign Distribution by Status */}
      <div className="row mb-4">
        <div className="col-lg-6">
          <div className="card shadow-md h-100">
            <div className="card-header py-2">
              <h6 className="m-0 fw-bold">
                <i className="fas fa-chart-pie me-2"></i>
                Campaign Distribution by Status
              </h6>
            </div>
            <div className="card-body p-2">
              <div className="row g-2">
                {/* Total Campaigns */}
                <div className="col-6">
                  <div className="text-center p-2 rounded h-100" style={{
                    background: 'var(--primary-500)',
                    boxShadow: 'var(--shadow-sm)',
                    color: 'var(--white)'
                  }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fas fa-flag me-2"></i>
                      <span className="h4 mb-0 fw-bold">{stats.total_campaigns}</span>
                    </div>
                    <div className="fw-semibold" style={{ opacity: 0.95, fontSize: '0.75rem' }}>
                      Total
                    </div>
                  </div>
                </div>

                {/* Active Campaigns */}
                <div className="col-6">
                  <div className="text-center p-2 rounded h-100" style={{
                    background: 'var(--primary-600)',
                    boxShadow: 'var(--shadow-sm)',
                    color: 'var(--white)'
                  }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fas fa-play-circle me-2"></i>
                      <span className="h4 mb-0 fw-bold">{stats.active_campaigns}</span>
                    </div>
                    <div className="fw-semibold" style={{ opacity: 0.95, fontSize: '0.75rem' }}>
                      Active
                    </div>
                  </div>
                </div>

                {/* Attestation Campaigns */}
                <div className="col-6">
                  <div className="text-center p-2 rounded h-100" style={{
                    background: 'var(--warning)',
                    boxShadow: 'var(--shadow-sm)',
                    color: 'var(--white)'
                  }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fas fa-clipboard-check me-2"></i>
                      <span className="h4 mb-0 fw-bold">{stats.attestation_campaigns}</span>
                    </div>
                    <div className="fw-semibold" style={{ opacity: 0.95, fontSize: '0.75rem' }}>
                      Attestation
                    </div>
                  </div>
                </div>

                {/* Completed Campaigns */}
                <div className="col-6">
                  <div className="text-center p-2 rounded h-100" style={{
                    background: 'var(--success)',
                    boxShadow: 'var(--shadow-sm)',
                    color: 'var(--white)'
                  }}>
                    <div className="d-flex align-items-center justify-content-center">
                      <i className="fas fa-check-circle me-2"></i>
                      <span className="h4 mb-0 fw-bold">{stats.completed_campaigns}</span>
                    </div>
                    <div className="fw-semibold" style={{ opacity: 0.95, fontSize: '0.75rem' }}>
                      Completed
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Distribution by Compliance Framework */}
        <div className="col-lg-6">
          <div className="card shadow-md h-100">
            <div className="card-header py-2">
              <h6 className="m-0 fw-bold">
                <i className="fas fa-shield-alt me-2"></i>
                Campaign Distribution by Compliance Framework
              </h6>
            </div>
            <div className="card-body p-2">
              <div className="row g-2">
                {Object.keys(complianceStats).length === 0 ? (
                  <div className="col-12 text-center text-muted py-3">
                    <i className="fas fa-info-circle me-2"></i>
                    No compliance data available
                  </div>
                ) : (
                  Object.entries(complianceStats).map(([framework, count], index) => {
                    // Professional audit/compliance color palette - lighter, readable tones
                    const colors = [
                      '#4a6fa5', // Steel blue
                      '#5a8f7b', // Sage green
                      '#7c6f64', // Warm taupe
                      '#6b7394', // Muted indigo
                      '#5d8a8a', // Dusty teal
                      '#708090', // Slate gray
                      '#6a8caf', // Cadet blue
                      '#7d9b76'  // Moss green
                    ];
                    const bgColor = colors[index % colors.length];
                    return (
                      <div className="col-6" key={framework}>
                        <div className="text-center p-2 rounded h-100" style={{
                          background: bgColor,
                          boxShadow: 'var(--shadow-sm)',
                          color: 'var(--white)'
                        }}>
                          <div className="d-flex align-items-center justify-content-center">
                            <i className="fas fa-shield-alt me-2"></i>
                            <span className="h4 mb-0 fw-bold">{count}</span>
                          </div>
                          <div className="fw-semibold" style={{ opacity: 0.95, fontSize: '0.75rem' }}>
                            {framework}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="card shadow-md mb-4">
        <div className="card-header">
          <h5 className="m-0 fw-bold">
            <i className="fas fa-flag me-2"></i>
            All UAR Campaigns
          </h5>
        </div>
        <div className="card-body p-0">
          {campaigns.length === 0 ? (
            <div className="alert alert-info text-center m-4">
              <i className="fas fa-info-circle me-2"></i>
              No campaigns found. Create your first campaign to get started!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="border-0 py-3 px-4" style={{ color: '#2d3748', fontWeight: '600' }}>Campaign Name</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>Description</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>Start Date</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>End Date</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>Status</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>Created By</th>
                    <th className="border-0 py-3" style={{ color: '#2d3748', fontWeight: '600' }}>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign, index) => (
                    <tr key={campaign.campaign_id}>
                      <td className="py-3 px-4">
                        <strong style={{ color: 'var(--text-primary)' }}>
                          {campaign.campaign_name}
                        </strong>
                      </td>
                      <td className="py-3">
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {campaign.campaign_description || campaign.description || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3">
                        <i className="fas fa-calendar-alt me-2" style={{ color: 'var(--text-muted)' }}></i>
                        {formatDate(campaign.start_date)}
                      </td>
                      <td className="py-3">
                        <i className="fas fa-calendar-alt me-2" style={{ color: 'var(--text-muted)' }}></i>
                        {formatDate(campaign.end_date)}
                      </td>
                      <td className="py-3">{getStatusBadge(campaign.status)}</td>
                      <td className="py-3">
                        <i className="fas fa-user me-2" style={{ color: 'var(--text-muted)' }}></i>
                        {campaign.created_by || 'N/A'}
                      </td>
                      <td className="py-3">
                        <i className="fas fa-clock me-2" style={{ color: 'var(--text-muted)' }}></i>
                        {formatDate(campaign.created_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
